import { StateCreator } from "zustand";
import {
  Playbook,
  CreatePlaybookRequest,
  CreatePlaybookAIRequest,
  UpdatePlaybookRequest,
  CreateRuleRequest,
  UpdateRuleRequest,
  getPlaybooks,
  getPlaybook,
  createPlaybook as createPlaybookApi,
  createPlaybookWithAI as createPlaybookWithAIApi,
  updatePlaybook as updatePlaybookApi,
  deletePlaybook as deletePlaybookApi,
  addRuleToPlaybook,
  updateRuleInPlaybook,
  deleteRuleFromPlaybook,
} from "../services/Playbook";

export interface PlaybooksSlice {
  // State
  playbooks: Playbook[];
  selectedPlaybook: Playbook | null;
  isLoading: boolean;
  error: string | null;

  // Playbook actions
  fetchPlaybooks: () => Promise<void>;
  fetchPlaybook: (playbookId: string) => Promise<void>;
  createPlaybook: (data: CreatePlaybookRequest) => Promise<Playbook | null>;
  createPlaybookWithAI: (data: CreatePlaybookAIRequest) => Promise<Playbook | null>;
  updatePlaybook: (playbookId: string, data: UpdatePlaybookRequest) => Promise<Playbook | null>;
  deletePlaybook: (playbookId: string) => Promise<boolean>;
  setSelectedPlaybook: (playbook: Playbook | null) => void;

  // Rule actions
  createRule: (playbookId: string, rule: CreateRuleRequest) => Promise<Playbook | null>;
  updateRule: (playbookId: string, ruleId: string, updates: Partial<UpdateRuleRequest>) => Promise<Playbook | null>;
  deleteRule: (playbookId: string, ruleId: string) => Promise<Playbook | null>;

  // Clear error
  clearError: () => void;
}

export const createPlaybooksSlice: StateCreator<PlaybooksSlice> = (set, get) => ({
  // Initial state
  playbooks: [],
  selectedPlaybook: null,
  isLoading: false,
  error: null,

  // Fetch all playbooks
  fetchPlaybooks: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await getPlaybooks();
      if (response.success && response.data) {
        set({ playbooks: response.data, isLoading: false });
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to fetch playbooks";
        set({ error: errorMsg, isLoading: false });
      }
    } catch (error) {
      set({ error: "An unexpected error occurred", isLoading: false });
    }
  },

  // Fetch a single playbook
  fetchPlaybook: async (playbookId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getPlaybook(playbookId);
      if (response.success && response.data) {
        set({ selectedPlaybook: response.data, isLoading: false });
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to fetch playbook";
        set({ error: errorMsg, isLoading: false });
      }
    } catch (error) {
      set({ error: "An unexpected error occurred", isLoading: false });
    }
  },

  // Create a new playbook manually
  createPlaybook: async (data: CreatePlaybookRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createPlaybookApi(data);
      if (response.success && response.data) {
        // Add to playbooks array
        set((state) => ({
          playbooks: [...state.playbooks, response.data!],
          isLoading: false,
        }));
        return response.data;
      } else {
        // Prefer the human detail (e.g. "Free trial limited to 2 playbooks...")
        // over the code in title (e.g. "TRIAL_PLAYBOOK_LIMIT").
        const errorMsg =
          response.errors?.[0]?.detail ||
          response.errors?.[0]?.title ||
          "Failed to create playbook";
        set({ error: errorMsg, isLoading: false });
        return null;
      }
    } catch (error) {
      set({ error: "An unexpected error occurred", isLoading: false });
      return null;
    }
  },

  // Create a new playbook with AI
  createPlaybookWithAI: async (data: CreatePlaybookAIRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createPlaybookWithAIApi(data);
      if (response.success && response.data) {
        // Add to playbooks array
        set((state) => ({
          playbooks: [...state.playbooks, response.data!],
          selectedPlaybook: response.data!,
          isLoading: false,
        }));
        return response.data;
      } else {
        const errorMsg =
          response.errors?.[0]?.detail ||
          response.errors?.[0]?.title ||
          "Failed to create playbook with AI";
        set({ error: errorMsg, isLoading: false });
        return null;
      }
    } catch (error) {
      set({ error: "An unexpected error occurred", isLoading: false });
      return null;
    }
  },

  // Update a playbook
  updatePlaybook: async (playbookId: string, data: UpdatePlaybookRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updatePlaybookApi(playbookId, data);
      if (response.success && response.data) {
        // Update in playbooks array
        set((state) => ({
          playbooks: state.playbooks.map((p) => (p.id === playbookId ? response.data! : p)),
          selectedPlaybook: state.selectedPlaybook?.id === playbookId ? response.data! : state.selectedPlaybook,
          isLoading: false,
        }));
        return response.data;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to update playbook";
        set({ error: errorMsg, isLoading: false });
        return null;
      }
    } catch (error) {
      set({ error: "An unexpected error occurred", isLoading: false });
      return null;
    }
  },

  // Delete a playbook
  deletePlaybook: async (playbookId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await deletePlaybookApi(playbookId);
      if (response.success) {
        // Remove from playbooks array
        set((state) => ({
          playbooks: state.playbooks.filter((p) => p.id !== playbookId),
          selectedPlaybook: state.selectedPlaybook?.id === playbookId ? null : state.selectedPlaybook,
          isLoading: false,
        }));
        return true;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to delete playbook";
        set({ error: errorMsg, isLoading: false });
        return false;
      }
    } catch (error) {
      set({ error: "An unexpected error occurred", isLoading: false });
      return false;
    }
  },

  // Set selected playbook
  setSelectedPlaybook: (playbook: Playbook | null) => {
    set({ selectedPlaybook: playbook });
  },

  // Create a rule
  createRule: async (playbookId: string, rule: CreateRuleRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await addRuleToPlaybook(playbookId, rule);
      if (response.success && response.data) {
        // Update in playbooks array and selected playbook
        set((state) => ({
          playbooks: state.playbooks.map((p) => (p.id === playbookId ? response.data! : p)),
          selectedPlaybook: state.selectedPlaybook?.id === playbookId ? response.data! : state.selectedPlaybook,
          isLoading: false,
        }));
        return response.data;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to create rule";
        set({ error: errorMsg, isLoading: false });
        return null;
      }
    } catch (error) {
      set({ error: "An unexpected error occurred", isLoading: false });
      return null;
    }
  },

  // Update a rule
  updateRule: async (playbookId: string, ruleId: string, updates: Partial<UpdateRuleRequest>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateRuleInPlaybook(playbookId, ruleId, updates);
      if (response.success && response.data) {
        // Update in playbooks array and selected playbook
        set((state) => ({
          playbooks: state.playbooks.map((p) => (p.id === playbookId ? response.data! : p)),
          selectedPlaybook: state.selectedPlaybook?.id === playbookId ? response.data! : state.selectedPlaybook,
          isLoading: false,
        }));
        return response.data;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to update rule";
        set({ error: errorMsg, isLoading: false });
        return null;
      }
    } catch (error) {
      set({ error: "An unexpected error occurred", isLoading: false });
      return null;
    }
  },

  // Delete a rule
  deleteRule: async (playbookId: string, ruleId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await deleteRuleFromPlaybook(playbookId, ruleId);
      if (response.success && response.data) {
        // Update in playbooks array and selected playbook
        set((state) => ({
          playbooks: state.playbooks.map((p) => (p.id === playbookId ? response.data! : p)),
          selectedPlaybook: state.selectedPlaybook?.id === playbookId ? response.data! : state.selectedPlaybook,
          isLoading: false,
        }));
        return response.data;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to delete rule";
        set({ error: errorMsg, isLoading: false });
        return null;
      }
    } catch (error) {
      set({ error: "An unexpected error occurred", isLoading: false });
      return null;
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },
});
